import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { 
  ArrowLeft, ArrowRight, Activity, Brain, CheckCircle,
  AlertTriangle, Heart, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CARE_LEVELS = [
  { value: 'companionship', label: 'Companionship & Light Support' },
  { value: 'adl_support', label: 'ADL Support (Bathing, Dressing, etc.)' },
  { value: 'skilled_nursing', label: 'Skilled Nursing Care' },
  { value: 'post_op', label: 'Post-Surgery Recovery' },
  { value: 'chronic_disease', label: 'Chronic Disease Management' },
  { value: 'hospice', label: 'Hospice / Palliative Care' },
  { value: 'mental_health', label: 'Mental Health Support' }
];

const FUNCTIONAL_LIMITATIONS = [
  'Mobility Issues',
  'Memory Problems',
  'Vision Impairment',
  'Hearing Impairment',
  'Speech Difficulties',
  'Incontinence',
  'Feeding Assistance',
  'Medication Management',
  'Wound Care',
  'Pain Management'
];

const MENTAL_HEALTH_FACTORS = [
  'Depression',
  'Anxiety',
  'Dementia/Alzheimer\'s',
  'Behavioral Issues',
  'Social Isolation',
  'Grief/Loss',
  'PTSD',
  'None'
];

export default function PatientAssessment() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  
  const [formData, setFormData] = useState({
    diagnosis: '',
    care_level_needed: '',
    functional_limitations: [],
    mental_health_factors: [],
    cultural_preferences: '',
    language_preferences: ['English'],
    location: '',
    budget: '',
    schedule_needs: '',
    additional_notes: ''
  });

  const totalSteps = 4;
  const progress = (step / totalSteps) * 100;

  const handleNext = () => {
    if (step < totalSteps) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const toggleLimitation = (limitation) => {
    setFormData(prev => ({
      ...prev,
      functional_limitations: prev.functional_limitations.includes(limitation)
        ? prev.functional_limitations.filter(l => l !== limitation)
        : [...prev.functional_limitations, limitation]
    }));
  };

  const toggleMentalHealth = (factor) => {
    setFormData(prev => ({
      ...prev,
      mental_health_factors: prev.mental_health_factors.includes(factor)
        ? prev.mental_health_factors.filter(f => f !== factor)
        : [...prev.mental_health_factors, factor]
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/patient/assessment`, formData, { ...getAuthHeader(), timeout: 90000 });
      setResults(response.data.results);
      toast.success('Assessment complete!');
      setStep(5); // Show results
    } catch (error) {
      const msg = error.code === 'ECONNABORTED'
        ? 'The AI is taking longer than usual. Please try again in a moment.'
        : (error.response?.data?.detail || 'Assessment failed');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-cn-sage-100 dark:from-cn-ink-900 dark:via-cn-ink-900 dark:to-cn-ink-800 py-8 px-4" data-testid="patient-assessment">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button 
            onClick={() => step === 1 ? navigate('/patient/dashboard') : handleBack()}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-cn-ink-800 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white font-display">
              Care Needs Assessment
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {step <= 4 ? `Step ${step} of ${totalSteps}` : 'Assessment Complete'}
            </p>
          </div>
          <div className="w-12 h-12 bg-cn-sage-700 rounded-xl flex items-center justify-center">
            <Brain className="w-6 h-6 text-white" />
          </div>
        </div>

        {/* Progress Bar */}
        {step <= 4 && (
          <div className="mb-8">
            <Progress value={progress} className="h-2" />
          </div>
        )}

        {/* Step 1: Medical Information */}
        {step === 1 && (
          <Card className="border-0 shadow-xl bg-white/80 dark:bg-cn-ink-800/80 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-display">
                <Heart className="w-5 h-5 text-red-500" />
                Medical Information
              </CardTitle>
              <CardDescription>Tell us about the primary health condition</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Primary Diagnosis / Condition</Label>
                <Textarea
                  data-testid="diagnosis-input"
                  placeholder="e.g., Post hip replacement surgery, Alzheimer's disease, Chronic heart failure..."
                  value={formData.diagnosis}
                  onChange={(e) => setFormData({...formData, diagnosis: e.target.value})}
                  className="min-h-[100px] bg-slate-50 dark:bg-cn-ink-700/50"
                />
              </div>

              <div className="space-y-2">
                <Label>Level of Care Needed</Label>
                <Select 
                  value={formData.care_level_needed}
                  onValueChange={(value) => setFormData({...formData, care_level_needed: value})}
                >
                  <SelectTrigger data-testid="care-level-select" className="bg-slate-50 dark:bg-cn-ink-700/50">
                    <SelectValue placeholder="Select care level" />
                  </SelectTrigger>
                  <SelectContent>
                    {CARE_LEVELS.map((level) => (
                      <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button 
                onClick={handleNext}
                data-testid="next-btn-step1"
                disabled={!formData.diagnosis || !formData.care_level_needed}
                className="w-full bg-cn-sage-700 hover:bg-cn-sage-800 text-white rounded-xl h-12"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Functional Limitations */}
        {step === 2 && (
          <Card className="border-0 shadow-xl bg-white/80 dark:bg-cn-ink-800/80 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-display">
                <Activity className="w-5 h-5 text-cn-primary" />
                Functional Needs
              </CardTitle>
              <CardDescription>Select all that apply</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Functional Limitations</Label>
                <div className="grid grid-cols-2 gap-3">
                  {FUNCTIONAL_LIMITATIONS.map((limitation) => (
                    <div
                      key={limitation}
                      onClick={() => toggleLimitation(limitation)}
                      className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                        formData.functional_limitations.includes(limitation)
                          ? 'border-cn-primary bg-cn-sage-50 dark:bg-cn-sage-900/30'
                          : 'border-slate-200 dark:border-cn-ink-700 hover:border-slate-300 dark:hover:border-slate-600'
                      }`}
                    >
                      <Checkbox 
                        checked={formData.functional_limitations.includes(limitation)}
                        className="pointer-events-none"
                      />
                      <span className="text-sm text-slate-700 dark:text-slate-300">{limitation}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Mental Health Considerations</Label>
                <div className="grid grid-cols-2 gap-3">
                  {MENTAL_HEALTH_FACTORS.map((factor) => (
                    <div
                      key={factor}
                      onClick={() => toggleMentalHealth(factor)}
                      className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                        formData.mental_health_factors.includes(factor)
                          ? 'border-cn-primary bg-cn-sage-50 dark:bg-cn-sage-900/30'
                          : 'border-slate-200 dark:border-cn-ink-700 hover:border-slate-300 dark:hover:border-slate-600'
                      }`}
                    >
                      <Checkbox 
                        checked={formData.mental_health_factors.includes(factor)}
                        className="pointer-events-none"
                      />
                      <span className="text-sm text-slate-700 dark:text-slate-300">{factor}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Button 
                onClick={handleNext}
                data-testid="next-btn-step2"
                disabled={formData.functional_limitations.length === 0}
                className="w-full bg-cn-sage-700 hover:bg-cn-sage-800 text-white rounded-xl h-12"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Preferences */}
        {step === 3 && (
          <Card className="border-0 shadow-xl bg-white/80 dark:bg-cn-ink-800/80 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-display">
                <FileText className="w-5 h-5 text-cn-primary" />
                Preferences & Location
              </CardTitle>
              <CardDescription>Help us find the best match</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Location (City, State)</Label>
                <Input
                  data-testid="location-input"
                  placeholder="e.g., Austin, TX"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  className="bg-slate-50 dark:bg-cn-ink-700/50"
                />
              </div>

              <div className="space-y-2">
                <Label>Cultural / Language Preferences</Label>
                <Input
                  data-testid="cultural-input"
                  placeholder="e.g., Spanish-speaking, familiar with Asian culture..."
                  value={formData.cultural_preferences}
                  onChange={(e) => setFormData({...formData, cultural_preferences: e.target.value})}
                  className="bg-slate-50 dark:bg-cn-ink-700/50"
                />
              </div>

              <div className="space-y-2">
                <Label>Budget Range</Label>
                <Select 
                  value={formData.budget}
                  onValueChange={(value) => setFormData({...formData, budget: value})}
                >
                  <SelectTrigger data-testid="budget-select" className="bg-slate-50 dark:bg-cn-ink-700/50">
                    <SelectValue placeholder="Select budget range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">$20-35/hour</SelectItem>
                    <SelectItem value="medium">$35-50/hour</SelectItem>
                    <SelectItem value="high">$50-75/hour</SelectItem>
                    <SelectItem value="premium">$75+/hour</SelectItem>
                    <SelectItem value="insurance">Insurance Coverage</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button 
                onClick={handleNext}
                data-testid="next-btn-step3"
                disabled={!formData.location || !formData.budget}
                className="w-full bg-cn-sage-700 hover:bg-cn-sage-800 text-white rounded-xl h-12"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Schedule & Review */}
        {step === 4 && (
          <Card className="border-0 shadow-xl bg-white/80 dark:bg-cn-ink-800/80 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-display">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Schedule & Final Details
              </CardTitle>
              <CardDescription>Almost done!</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Schedule Needs</Label>
                <Textarea
                  data-testid="schedule-input"
                  placeholder="e.g., Weekdays 9am-5pm, 24/7 care needed, Flexible schedule..."
                  value={formData.schedule_needs}
                  onChange={(e) => setFormData({...formData, schedule_needs: e.target.value})}
                  className="min-h-[80px] bg-slate-50 dark:bg-cn-ink-700/50"
                />
              </div>

              <div className="space-y-2">
                <Label>Additional Notes (Optional)</Label>
                <Textarea
                  data-testid="notes-input"
                  placeholder="Any other important information about care needs..."
                  value={formData.additional_notes}
                  onChange={(e) => setFormData({...formData, additional_notes: e.target.value})}
                  className="min-h-[80px] bg-slate-50 dark:bg-cn-ink-700/50"
                />
              </div>

              {/* Summary */}
              <div className="bg-slate-50 dark:bg-cn-ink-700/50 rounded-xl p-4 space-y-2">
                <h4 className="font-semibold text-slate-900 dark:text-white">Assessment Summary</h4>
                <div className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
                  <p><strong>Condition:</strong> {formData.diagnosis.substring(0, 50)}...</p>
                  <p><strong>Care Level:</strong> {CARE_LEVELS.find(l => l.value === formData.care_level_needed)?.label}</p>
                  <p><strong>Location:</strong> {formData.location}</p>
                  <p><strong>Limitations:</strong> {formData.functional_limitations.length} selected</p>
                </div>
              </div>

              <Button 
                onClick={handleSubmit}
                data-testid="submit-assessment-btn"
                disabled={loading || !formData.schedule_needs}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-xl h-12 shadow-lg shadow-orange-500/20"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Analyzing with AI...</span>
                  </div>
                ) : (
                  <>
                    <Brain className="w-4 h-4 mr-2" />
                    Complete Assessment
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 5: Results */}
        {step === 5 && results && (
          <Card className="border-0 shadow-xl bg-white/80 dark:bg-cn-ink-800/80 backdrop-blur-xl">
            <CardHeader className="text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <CardTitle className="text-2xl font-display">Assessment Complete!</CardTitle>
              <CardDescription>Here&apos;s your AI-powered care analysis</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-cn-sage-50 dark:bg-cn-sage-900/20 rounded-xl p-4 text-center">
                  <p className="text-sm text-cn-primary dark:text-cn-sage-300 font-medium">Care Complexity</p>
                  <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">{results.care_complexity_score}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">out of 100</p>
                </div>
                <div className={`rounded-xl p-4 text-center ${
                  results.urgency_level === 'High Risk' 
                    ? 'bg-red-50 dark:bg-red-900/20' 
                    : results.urgency_level === 'Moderate'
                    ? 'bg-orange-50 dark:bg-orange-900/20'
                    : 'bg-green-50 dark:bg-green-900/20'
                }`}>
                  <p className={`text-sm font-medium ${
                    results.urgency_level === 'High Risk' 
                      ? 'text-red-600 dark:text-red-400' 
                      : results.urgency_level === 'Moderate'
                      ? 'text-orange-600 dark:text-orange-400'
                      : 'text-green-600 dark:text-green-400'
                  }`}>Urgency Level</p>
                  <p className="text-xl font-bold text-slate-900 dark:text-white mt-1">{results.urgency_level}</p>
                </div>
              </div>

              <div className="bg-slate-50 dark:bg-cn-ink-700/50 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600 dark:text-slate-400">Recommended Care Level</span>
                  <span className="font-semibold text-slate-900 dark:text-white">{results.recommended_care_level}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600 dark:text-slate-400">Care Type</span>
                  <span className="font-semibold text-slate-900 dark:text-white capitalize">{results.care_type_recommendation?.replace('_', ' ')}</span>
                </div>
              </div>

              {results.reasoning && (
                <div className="bg-cn-sage-50 dark:bg-cn-sage-900/20 rounded-xl p-4">
                  <p className="text-sm font-medium text-cn-primary dark:text-cn-primary mb-2">AI Reasoning</p>
                  <p className="text-sm text-slate-600 dark:text-slate-300">{results.reasoning}</p>
                </div>
              )}

              <Button 
                onClick={() => navigate('/patient/dashboard')}
                data-testid="go-to-dashboard-btn"
                className="w-full bg-cn-sage-700 hover:bg-cn-sage-800 text-white rounded-xl h-12"
              >
                Find Your Matches
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
